import { ButtonERP } from "@finanedi.es/storybook"
import {
  faArrowLeft,
  faArrowsRotateReverse,
  faFileExport,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { GenerarFicheroModelo180CommandProps } from "api/Commands/GenerarFicheroModelo180Command"
import { postDeclaracionModelo180 } from "api/DeclaracionModelo"
import { useDeclaracion180 } from "api/DeclaracionModelo.hook"
import { MODELO_IMPUESTO } from "api/Types/ModeloImpuesto"
import { useUsuarioEmpresaActiva } from "api/Usuario.hook"
import Spinner from "common/components/Spinner"
import notification from "common/utils/notification"
import { TFunction } from "i18next"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useHistory, useLocation } from "react-router-dom"
import * as Yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { ModalPresentarModelo } from "../../ModalPresentarModelo"
import { PlantillaDeclaracion } from "../../PlantillaDeclaracion"
import PlantillaDeclaracionEstandar from "../../PlantillaDeclaracionEstandar"
import {
  Modelo180FormModel,
  postModelo180Adapter,
} from "../postModelo180.adapter"
import Perceptores180 from "./Perceptores180/Perceptores180"

const SECCIONES_TYPES = {
  IDENTIFICACION: 1,
  PERSONA: 2,
  COMPLEMENTARIO: 3,
  SUSTITUTIVA: 4,
  RESUMEN: 5,
  PERCEPTORES: 6,
}
const SECCIONES = (t: TFunction) => [
  {
    id: SECCIONES_TYPES.IDENTIFICACION,
    label: `${SECCIONES_TYPES.IDENTIFICACION} - ${t(
      "declaracionModelo180.sections.identification"
    )}`,
  },
  {
    id: SECCIONES_TYPES.PERSONA,
    label: `${SECCIONES_TYPES.PERSONA} - ${t(
      "declaracionModelo180.sections.person"
    )}`,
  },
  {
    id: SECCIONES_TYPES.COMPLEMENTARIO,
    label: `${SECCIONES_TYPES.COMPLEMENTARIO} - ${t(
      "declaracionModelo180.sections.complement"
    )}`,
  },
  {
    id: SECCIONES_TYPES.SUSTITUTIVA,
    label: `${SECCIONES_TYPES.SUSTITUTIVA} - ${t(
      "declaracionModelo180.sections.sustitute"
    )}`,
  },
  {
    id: SECCIONES_TYPES.RESUMEN,
    label: `${SECCIONES_TYPES.RESUMEN} - ${t(
      "declaracionModelo180.sections.summary"
    )}`,
  },
  {
    id: SECCIONES_TYPES.PERCEPTORES,
    label: `${SECCIONES_TYPES.PERCEPTORES} - ${t(
      "taxSummary.declaracion.perceptors"
    )}`,
  },
]
const mapRespToDto = () => ({})

const DeclaracionModelo180 = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const [tabSelected, setSelectedTab] = useState(SECCIONES_TYPES.IDENTIFICACION)
  const [showModalPresentar, setShowModalPresentar] = useState<boolean>(false)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const {
    activeCompany: { razonSocial, nif },
  } = useUsuarioEmpresaActiva()
  const { state } = useLocation() as { state?: { year?: number } }
  const {
    declaracion180Data,
    declaracion180DataIsLoading,
    declaracion180DataRefetch,
  } = useDeclaracion180({
    queryParams: {
      Anyo: Number(state?.year),
    },
    mapRespToDto,
    razonSocial,
    nif,
    year: state?.year,
  })

  const schemaModelo180 = Yup.object({
    nif: Yup.string().required(),
    razonSocial: Yup.string().required(),
    telefono: Yup.string().required(),
    apellidos: Yup.string().required(),
    perceptores: Yup.array().of(
      Yup.object({
        modalidad: Yup.string().required(),
        situacionInmueble: Yup.string().required(),
        tipoVia: Yup.string().required(),
        tipoNumeracion: Yup.string().required(),
        provinciaDos: Yup.string().required(),
      })
    ),
  }).required()

  const defaultValues = {
    apellidos: "",
    "01": declaracion180Data?.casilla01,
    "02": declaracion180Data?.casilla02,
    "03": declaracion180Data?.casilla03,
    complementariaCheck: false,
    ejercicio: state?.year?.toString() ?? "",
    nif,
    perceptores: declaracion180Data?.perceptores ?? [],
    razonSocial,
    sustitutivaCheck: false,
    telefono: "",
  }
  const {
    setValue,
    watch,
    reset,
    formState: { errors },
    trigger,
  } = useForm<$TSMigrationFixMe>({
    values: defaultValues,
    resolver: yupResolver(schemaModelo180),
    mode: "onChange",
    reValidateMode: "onChange",
  })
  const values = watch()

  const handlePresentarClick = async () => {
    const isValid = await trigger()
    if (!isValid) {
      notification.warning({
        body: t("notification.warningModelDeclaration"),
      })
      const identificacionValid = await trigger(["nif", "razonSocial"])
      if (!identificacionValid) {
        setSelectedTab(SECCIONES_TYPES.IDENTIFICACION)
        return
      }

      const personaValid = await trigger(["telefono", "apellidos"])
      if (!personaValid) {
        setSelectedTab(SECCIONES_TYPES.PERSONA)
        return
      }

      const perceptoresValid = await trigger(["perceptores"])
      if (!perceptoresValid) {
        setSelectedTab(SECCIONES_TYPES.PERCEPTORES)
        return
      }
    }
    setShowModalPresentar(!showModalPresentar)
  }

  const handleUpdateClick = async () => {
    setIsUpdating(true)
    try {
      const { data: updatedData } = await declaracion180DataRefetch()
      if (updatedData) {
        reset(defaultValues)
        notification.success({
          body: `${t("taxSummary.declaracion.successNotification")}`,
        })
        setIsUpdating(false)
      }
    } catch (error) {
      setIsUpdating(false)
      notification.errorServidor()
    }
  }

  return (
    <div className='tw-w-full tw-h-full tw-overflow-hidden tw-bg-white tw-text-left'>
      <div className='tw-h-fit tw-p-3 tw-w-full tw-bg-white tw-shadow'>
        <div className='tw-h-11 tw-flex tw-flex-row tw-w-full tw-items-center tw-justify-between'>
          <div className='tw-flex tw-flex-row tw-gap-4 tw-items-center tw-h-full'>
            <ButtonERP
              customize={{ size: "md", type: "transparent" }}
              onClick={() => history.goBack()}
              icon={<FontAwesomeIcon icon={faArrowLeft} />}
            />
            <span className='tw-rounded tw-h-fit tw-py-1 tw-px-2 tw-text-sm tw-text-white tw-bg-[#85A3CE]'>
              {MODELO_IMPUESTO.MODELO_180}
            </span>
            <span className='tw-text-base tw-font-medium tw-h-fit'>
              {t("declaracionModelo180.modal.title")} -{" "}
              {state?.year ?? new Date().getFullYear()}
            </span>
          </div>
          <div className='tw-flex tw-flex-row tw-gap-3'>
            <ButtonERP
              customize={{ size: "md", type: "transparent" }}
              label={`${t("taxSummary.declaracion.updateButton")}`}
              loading={isUpdating}
              disabled={isUpdating}
              icon={<FontAwesomeIcon icon={faArrowsRotateReverse} />}
              onClick={() => {
                handleUpdateClick()
              }}
            />
            <ButtonERP
              customize={{ size: "md", type: "primary" }}
              onClick={handlePresentarClick}
              icon={<FontAwesomeIcon icon={faFileExport} />}
              label={`${t("taxSummary.declaracion.presentar")}`}
              loading={isUpdating}
            />
          </div>
        </div>
      </div>
      <div className='tw-w-full tw-h-[calc(100%-68px)] tw-flex tw-flex-row'>
        <div className='tw-w-2/5 tw-p-6 tw-flex tw-flex-col tw-items-start tw-border-r tw-border-neutral-25'>
          <p className='tw-font-medium tw-text-base'>
            {t("taxSummary.declaracion.secciones")}
          </p>
          <div className='tw-flex tw-flex-col tw-items-start tw-mt-4 tw-w-full tw-gap-1 tw-text-left'>
            {SECCIONES(t).map(({ id, label }) => (
              <p
                key={id}
                className={`tw-h-10 tw-w-full tw-px-4 hover:tw-bg-neutral-25 ${
                  tabSelected === id ? "tw-bg-neutral-25" : ""
                } tw-flex tw-items-center tw-cursor-pointer tw-rounded tw-truncate`}
                onClick={() => setSelectedTab(id)}
              >
                {label}
              </p>
            ))}
          </div>
        </div>
        <div className='tw-w-4/5 tw-flex tw-flex-col tw-items-start tw-overflow-y-auto'>
          <div className='tw-w-full tw-text-base tw-font-medium tw-flex tw-items-center tw-justify-start tw-px-6 tw-py-4'>
            {SECCIONES(t).find((s) => s.id === tabSelected)?.label}
          </div>
          {declaracion180DataIsLoading ? (
            <Spinner />
          ) : (
            <>
              {tabSelected === SECCIONES_TYPES.IDENTIFICACION && (
                <PlantillaDeclaracionEstandar
                  setValue={setValue}
                  fields={[
                    {
                      id: "row1",
                      inputsOne: [
                        {
                          label: t(
                            "taxSummary.declaracion.devengoModelo.ejercicio"
                          ),
                          code: "ejercicio",
                          value: state?.year ?? "",
                          inputType: "text",
                          disabled: true,
                        },
                        {
                          label: t("taxSummary.declaracion.nif"),
                          code: "nif",
                          value: values.nif,
                          inputType: "text",
                          mandatory: true,
                          error: errors?.nif?.message,
                        },
                        {
                          label: t("taxSummary.declaracion.name"),
                          code: "razonSocial",
                          value: values.razonSocial,
                          inputType: "text",
                          mandatory: true,
                          error: errors?.razonSocial?.message as string,
                          colSpan: "tw-col-span-2",
                        },
                      ],
                    },
                  ]}
                />
              )}
              {tabSelected === SECCIONES_TYPES.PERSONA && (
                <PlantillaDeclaracionEstandar
                  setValue={setValue}
                  fields={[
                    {
                      id: "row1",
                      inputsOne: [
                        {
                          label: t("taxSummary.declaracion.telephone"),
                          code: "telefono",
                          value: values.telefono,
                          inputType: "text",
                          disabled: false,
                          mandatory: true,
                          error: errors?.telefono?.message as string,
                        },
                        {
                          label: t("declaracionModelo180.name"),
                          code: "apellidos",
                          value: values.apellidos,
                          inputType: "text",
                          mandatory: true,
                          colSpan: "tw-col-span-3",
                          error: errors?.apellidos?.message as string,
                        },
                      ],
                    },
                  ]}
                />
              )}
              {tabSelected === SECCIONES_TYPES.COMPLEMENTARIO && (
                <PlantillaDeclaracion
                  setValue={setValue}
                  fields={[
                    {
                      label: t("declaracionModelo180.complementaria"),
                      code: "complementariaCheck",
                      value: values.complementariaCheck,
                      inputType: "checkbox",
                    },
                    {
                      label: t("declaracionModelo180.numeroJustificante"),
                      code: "complementariaDeclaracionAnterior",
                      value: values.complementariaDeclaracionAnterior,
                      inputType: "number",
                    },
                  ]}
                />
              )}
              {tabSelected === SECCIONES_TYPES.SUSTITUTIVA && (
                <PlantillaDeclaracion
                  setValue={setValue}
                  fields={[
                    {
                      label: t("declaracionModelo180.sustitutiva"),
                      code: "sustitutivaCheck",
                      value: values.sustitutivaCheck,
                      inputType: "checkbox",
                    },
                    {
                      label: t("declaracionModelo180.numeroJustificante"),
                      code: "sustitutivaDeclaracionAnterior",
                      value: values.sustitutivaDeclaracionAnterior,
                      inputType: "number",
                    },
                  ]}
                />
              )}
              {tabSelected === SECCIONES_TYPES.RESUMEN && (
                <PlantillaDeclaracion
                  setValue={setValue}
                  fields={[
                    {
                      label: t("declaracionModelo180.dataSummary.box1"),
                      code: "01",
                      value: values["01"],
                      inputType: "numberGroup",
                    },
                    {
                      label: t("declaracionModelo180.dataSummary.box2"),
                      code: "02",
                      value: values["02"],
                      inputNumber: "02",
                    },
                    {
                      label: t("declaracionModelo180.dataSummary.box3"),
                      code: "03",
                      value: values["03"],
                      inputNumber: "03",
                    },
                  ]}
                />
              )}
              {tabSelected === SECCIONES_TYPES.PERCEPTORES && (
                <Perceptores180
                  setValue={setValue}
                  watch={watch}
                  errors={errors}
                />
              )}
              <span className='tw-text-xs tw-text-left tw-w-full tw-py-1 tw-px-6'>
                {t("taxSummary.declaracion.infoMsg")}
              </span>
            </>
          )}
        </div>
      </div>
      <ModalPresentarModelo<
        Modelo180FormModel,
        GenerarFicheroModelo180CommandProps
      >
        show={showModalPresentar}
        setShow={setShowModalPresentar}
        nombreDocumento='modelo180.txt'
        postDeclaracion={postDeclaracionModelo180}
        postDeclaracionAdapter={postModelo180Adapter}
        modalTitle='declaracionModelo180.modal.annual'
        infoText='taxSummary.declaracion.modal.infoTextTwo'
        values={values}
      />
    </div>
  )
}
export default DeclaracionModelo180
